import React from "react";
import { Check } from "lucide-react";
import { buttonClasses, labelClasses } from "../constants";
import { ThemeColor } from "../OutputLanguageSelector";

export interface Step {
  id: string;
  title: string;
  icon?: React.ReactNode;
}

export interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  themeColor: ThemeColor;
  className?: string;
  variant?: "horizontal" | "vertical";
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  themeColor,
  className = "",
  variant = "horizontal",
}) => {
  const getStepStatusColor = (stepIndex: number) => {
    if (stepIndex < currentStep) {
      // Completed step - use solid background with white text
      return `${buttonClasses[themeColor]} text-white`;
    } else if (stepIndex === currentStep) {
      // Current step - use light background with colored text and ring
      const colorMatch = buttonClasses[themeColor].match(/bg-(\w+)-\d+/);
      const colorName = colorMatch?.[1] || "blue";
      const lightBg = `bg-${colorName}-100 dark:bg-${colorName}-900/20`;
      return `${lightBg} ${labelClasses[themeColor]} ring-2 ring-${colorName}-500`;
    } else {
      // Future step
      return "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500";
    }
  };

  const getConnectorColor = (stepIndex: number) => {
    if (stepIndex < currentStep) {
      const colorMatch = buttonClasses[themeColor].match(/bg-(\w+)-\d+/);
      const colorName = colorMatch?.[1] || "blue";
      return `border-${colorName}-500`;
    }
    return "border-gray-300 dark:border-gray-600";
  };

  const getTextColor = (stepIndex: number) => {
    if (stepIndex <= currentStep) {
      return labelClasses[themeColor];
    }
    return "text-gray-500 dark:text-gray-400";
  };

  if (variant === "vertical") {
    return (
      <div className={`flex flex-col ${className}`}>
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-start">
            <div className="flex flex-col items-center">
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  transition-all duration-300 relative z-10
                  ${getStepStatusColor(index)}
                `}
              >
                {index < currentStep ? (
                  <Check size={16} />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-0.5 h-12 border-l-2 mt-2 ${getConnectorColor(index)}`}
                />
              )}
            </div>
            <div className="ml-4 pb-8">
              <h3 className={`text-sm font-medium ${getTextColor(index)}`}>
                {step.title}
              </h3>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Horizontal variant
  return (
    <div className={`flex items-center justify-between ${className}`}>
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center flex-1">
            <div
              className={`
                w-10 h-10 rounded-full flex items-center justify-center
                transition-all duration-300 relative
                ${getStepStatusColor(index)}
              `}
            >
              {step.icon ? (
                step.icon
              ) : index < currentStep ? (
                <Check size={16} />
              ) : (
                <span className="text-sm font-medium">{index + 1}</span>
              )}
            </div>
            <div className="text-center mt-2 w-full px-1 sm:px-2">
              <p
                className={`text-sm font-medium ${getTextColor(index)} leading-tight`}
              >
                {step.title}
              </p>
            </div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-6 sm:w-8 h-0.5 border-t-2 mx-1 sm:mx-2 flex-shrink-0 ${getConnectorColor(index)}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default StepIndicator;
